import { Box, FormControl, FormHelperText, FormLabel, Input, List, ListItem, Spinner, Text } from '@chakra-ui/react';
import { type Component, filterComponentsBySearchCfn } from '@rta/rta-blueprint-library';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { lang } from '@lang';

export type SkuSearchInputProps = {
	components: Array<Component>;
	value: string | null;
	excludeSku?: string;
	onChange: (sku: string | null) => void;
	enabled?: boolean;
};

export const SkuSearchInput = (props: SkuSearchInputProps): JSX.Element => {
	const { components, value, excludeSku, onChange, enabled = true } = props;

	const strings = lang.contextPanels.customAppliance.placeholderModel;

	const [inputValue, setInputValue] = useState<string>(value ?? '');
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	// Close dropdown on outside click
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const filteredComponents = useMemo((): Array<Component> => {
		const term = inputValue.trim();
		if (term === '') return [];

		const filtered = filterComponentsBySearchCfn(components, term);

		// Self-reference guard: the component cannot be its own placeholder (locked decision 6)
		return filtered.filter((c) => c.sku !== excludeSku).slice(0, 10);
	}, [inputValue, components, excludeSku]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const next = e.target.value;
		setInputValue(next);
		setIsOpen(true);
		if (next.trim() === '') {
			onChange(null);
		}
	};

	const handleSelect = (sku: string): void => {
		setInputValue(sku);
		setIsOpen(false);
		onChange(sku);
	};

	const handleClear = (): void => {
		setInputValue('');
		setIsOpen(false);
		onChange(null);
	};

	return (
		<Box ref={containerRef} position="relative">
			<FormControl>
				<FormLabel fontSize="small" mb={1}>{strings.label}:</FormLabel>
				<Input
					size="xs"
					value={inputValue}
					onChange={handleInputChange}
					onFocus={() => { if (inputValue.trim() !== '') setIsOpen(true); }}
					placeholder={strings.placeholder}
					autoComplete="off"
					isDisabled={!enabled}
				/>
				<FormHelperText fontSize="xs" mt={1}>{strings.helperText}</FormHelperText>
				{value && (
					<Text
						fontSize="xs"
						color="gray.500"
						mt={1}
						cursor={enabled ? 'pointer' : 'default'}
						onClick={enabled ? handleClear : undefined}
						display="inline"
					>
						Current: <strong>{value}</strong>
						{enabled && (
							<> — <span style={{ color: 'var(--chakra-colors-blue-400)' }}>{strings.clear}</span></>
						)}
					</Text>
				)}
			</FormControl>
			{isOpen && filteredComponents.length === 0 && inputValue.trim() !== '' && (
				<Spinner size="xs" position="absolute" right={2} top="34px" color="gray.400" />
			)}
			{isOpen && filteredComponents.length > 0 && (
				<Box
					position="absolute"
					zIndex={10}
					w="100%"
					bg="white"
					border="1px solid"
					borderColor="gray.200"
					borderRadius="md"
					boxShadow="md"
					mt={1}
					maxH="200px"
					overflowY="auto"
				>
					<List>
						{filteredComponents.map((component) => (
							<ListItem
								key={component.sku}
								px={3}
								py={2}
								cursor="pointer"
								_hover={{ bg: 'gray.50' }}
								onMouseDown={(e) => {
									// Prevent blur firing before click registers
									e.preventDefault();
									handleSelect(component.sku);
								}}
							>
								<Text fontSize="sm" fontWeight="semibold">{component.sku}</Text>
								{component.description && (
									<Text fontSize="xs" color="gray.500" noOfLines={1}>{component.description}</Text>
								)}
							</ListItem>
						))}
					</List>
				</Box>
			)}
		</Box>
	);
};
